/**
 * Which page is showing, kept in the address.
 *
 * The sidebar items are real links, as they are in the template, so a reload stays on the
 * same page and the back button goes back. A hash is used rather than paths so the server
 * never has to know the page names: it serves one file, and the iPad can load that one file
 * from its own cache with no signal.
 */

import { useSyncExternalStore } from "react";

export type Page =
  | "home"
  | "progress"
  | "pos"
  | "sales"
  | "people"
  | "reports"
  | "claims"
  | "stock"
  | "purchases"
  | "expenses"
  | "accounts"
  | "bank"
  | "categories"
  | "devices"
  | "company"
  | "users"
  | "password"
  // D's menu of 15 October 2026: sales by where they were made, buybacks on their own, and
  // the money of each account.
  | "retail"
  | "tradeshow"
  | "tiktok"
  | "buybacks"
  | "money";

const PAGES: readonly Page[] = ["home", "progress", "pos", "sales", "people", "reports", "claims", "stock", "purchases", "expenses", "accounts", "bank", "categories", "devices", "company", "users", "password", "retail", "tradeshow", "tiktok", "buybacks", "money"];

/** A value after the page in the address: #/bank?account=... */
export function hashParam(name: string): string {
  return new URLSearchParams(window.location.hash.split("?")[1] ?? "").get(name) ?? "";
}

/** The whole address after #, so a page can start again when only its account changes. */
export function useHash(): string {
  return useSyncExternalStore(
    (listener) => {
      window.addEventListener("hashchange", listener);
      return () => window.removeEventListener("hashchange", listener);
    },
    () => window.location.hash,
  );
}

function read(): Page {
  const name = window.location.hash.replace(/^#\/?/, "").split(/[/?]/)[0] as Page;
  return PAGES.includes(name) ? name : "home";
}

export function hrefOf(page: Page): string {
  return `#/${page}`;
}

export function go(page: Page): void {
  window.location.hash = `/${page}`;
}

export function usePage(): Page {
  return useSyncExternalStore(
    (listener) => {
      window.addEventListener("hashchange", listener);
      return () => window.removeEventListener("hashchange", listener);
    },
    read,
  );
}
